// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { handleAPIError, toDialogErrorMessage } from '@/api/error-utils';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import { FormCurrencyTextField } from '@/components/form-currency-text-field';
import { FormDatePicker } from '@/components/form-date-picker';
import { FormDateTimePicker } from '@/components/form-date-time-picker';
import { FormShippingCarrierSearch } from '@/components/form-shipping-carrier-search';
import { FormTextField } from '@/components/form-text-field';
import { Link } from '@/components/link';
import { InfoOutlined as InfoOutlinedIcon } from '@/constants/hardfin-standard-icons';
import {
  ShippingCarrier,
  shippingCarrierSchema,
  toTrackingNumberDetailsRequest,
} from '@/constants/shipping-carrier';
import { useToast } from '@/contexts/toast-context';
import {
  SerializableCurrency,
  clearableCurrencyInputSchema,
} from '@/currency/api/types';
import { toCurrencyInputValue } from '@/currency/utilities/to-form-value';
import { useSupportError } from '@/hooks/use-support-error';
import { FormLocationSearch } from '@/location/components/form-location-search';
import { getNewLocationURL } from '@/location/utilities/get-new-location-url';
import { PurchaseOrder } from '@/purchase-order/api/types';
import { FormPurchaseOrderSearch } from '@/purchase-order/components/form-purchase-order-search';
import { usePurchaseOrder } from '@/purchase-order/hooks/use-purchase-order';
import { purchaseOrdersQueryKey } from '@/purchase-order/query-keys';
import { ReturnOrder } from '@/return-order/api/types';
import { FormReturnOrderSearch } from '@/return-order/components/form-return-order-search';
import { useReturnOrder } from '@/return-order/hooks/use-return-order';
import { useReturnOrderInventory } from '@/return-order/hooks/use-return-order-inventory';
import { returnOrdersQueryKey } from '@/return-order/query-keys';
import { createShipment } from '@/shipment/api/create-shipment';
import { Shipment } from '@/shipment/api/types';
import {
  UpdateShipmentRequest,
  updateShipment,
} from '@/shipment/api/update-shipment';
import { InboundShipmentItemsTable } from '@/shipment/components/inbound-shipment-items-table';
import {
  defaultShipmentFormItem,
  mapShipmentItemsToFormItems,
  partOrProductItemSchema,
  validateShipmentItems,
} from '@/shipment/components/shipment-form-schemas';
import { shipmentDetailQueryKey } from '@/shipment/hooks/use-shipment';
import {
  shipmentsQueryKey,
  useShipments,
} from '@/shipment/hooks/use-shipments';
import { calculateShipmentQuantities } from '@/shipment/utilities/calculate-shipment-quantities';
import {
  calculateCascadeTimeSeconds,
  getTotalSerializedAssetCount,
  hasAssetEventGroupCascade,
} from '@/shipment/utilities/estimate-cascade-time';
import {
  CANCELLED_LOCK_REASON,
  MANAGED_ORDER_LOCK_REASON,
  THIRD_PARTY_ORDER_MESSAGES,
  canEditManifestInForm,
  hasLeftOpen,
} from '@/shipment/utilities/shipment-actions';
import { SparsePurchaseOrder, sparseLocationSchema } from '@/sparse/api/types';
import { extractSparseItem } from '@/sparse/utilities/to-sparse-item';
import { dateSchema, dateTimeSchema } from '@/utilities/date-time-schema';
import { formatDuration } from '@/utilities/format-duration';
import { pluralize } from '@/utilities/pluralize';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

/**
 * Mode for the inbound shipment dialog.
 * - 'purchase-order': Dialog is used for purchase order shipments (default)
 * - 'return-order': Dialog is used for return order shipments
 */
type InboundShipmentDialogMode = 'purchase-order' | 'return-order';

interface Props {
  /**
   * Whether the dialog is open.
   */
  isOpen: boolean;

  /**
   * Callback fired when the dialog is closed.
   */
  onClose: () => void;

  /**
   * Optional shipment to edit. If provided, the dialog will be in edit mode.
   */
  shipment?: Shipment;

  /**
   * Optional purchase order key for pre-populating the form in create mode.
   * When provided, the dialog will set this in the form field, which triggers data fetching.
   * Only used when mode is 'purchase-order'.
   */
  purchaseOrderKey?: string | null;

  /**
   * Optional return order key for pre-populating the form in create mode.
   * When provided, the dialog will set this in the form field, which triggers data fetching.
   * Only used when mode is 'return-order'.
   */
  returnOrderKey?: string | null;

  /**
   * Mode for the dialog. Determines whether to show PO or RO fields.
   * Defaults to 'purchase-order'.
   */
  mode?: InboundShipmentDialogMode;

  /**
   * Optional callback fired after a shipment is successfully created or updated.
   * Useful for invalidating related queries on the parent page.
   */
  onSuccess?: () => void;

  /**
   * Optional title override for the dialog. If not provided, defaults to
   * "Edit shipment" when editing or "Add shipment" when creating.
   */
  dialogTitle?: string;

  /**
   * Optional callback fired when the mutation in-flight state changes.
   * Used by the outer dialog wrapper to control the submit button loading state.
   */
  onSubmittingChange?: (isSubmitting: boolean) => void;
}

// Validates minimum fields needed to display the order label in the search field
const purchaseOrderIdentifierSchema = z.object({
  id: z.string(),
  key: z.string(),
  purchaseOrderNumber: z.string(),
  // Optional: a shipment's own order reference arrives without it
  thirdParty: z.object({ isEnabled: z.boolean() }).optional(),
});

const returnOrderIdentifierSchema = z.object({
  id: z.string(),
  key: z.string(),
  returnOrderNumber: z.string(),
  // Optional: a shipment's own order reference arrives without it
  thirdParty: z.object({ isEnabled: z.boolean() }).optional(),
});

// Built per shipment: the 3PL rule judges a move, so the current order passes.
const makeFormSchema = (currentOrderId: string | null) =>
  z
    .object({
      purchaseOrder: purchaseOrderIdentifierSchema.nullable(),
      returnOrder: returnOrderIdentifierSchema.nullable(),
      carrier: shippingCarrierSchema.nullable(),
      otherCarrier: z.string().nullable(),
      trackingNumber: z.string().nullable(),
      fromLocation: sparseLocationSchema.nullable(),
      toLocation: sparseLocationSchema.nullable(),
      expectedShippedOn: dateSchema.nullable(),
      expectedDeliveredOn: dateSchema.nullable(),
      shippedAt: dateTimeSchema.nullable(),
      deliveredAt: dateTimeSchema.nullable(),
      shipmentNote: z.string().nullable(),
      shippingCost: clearableCurrencyInputSchema,
      items: z
        .array(partOrProductItemSchema)
        .max(100, 'Shipments cannot have more than 100 line items'),
    })
    .superRefine((data, ctx) => {
      // Check if toLocation is set (not null)
      if (!data.toLocation) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'To location is required',
          path: ['toLocation'],
        });
      }

      // A 3PL creates its own shipments; only a move onto one of its orders is refused.
      if (
        data.purchaseOrder?.thirdParty?.isEnabled &&
        data.purchaseOrder.id !== currentOrderId
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: THIRD_PARTY_ORDER_MESSAGES.purchaseOrder,
          path: ['purchaseOrder'],
        });
      }
      if (
        data.returnOrder?.thirdParty?.isEnabled &&
        data.returnOrder.id !== currentOrderId
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: THIRD_PARTY_ORDER_MESSAGES.returnOrder,
          path: ['returnOrder'],
        });
      }

      // Validate expected delivery date is not before expected ship date
      if (data.expectedDeliveredOn && data.expectedShippedOn) {
        if (data.expectedDeliveredOn < data.expectedShippedOn) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              'Expected delivery date cannot be before expected ship date',
            path: ['expectedDeliveredOn'],
          });
        }
      }

      // Validate actual delivery date is not before actual ship date
      if (data.deliveredAt && data.shippedAt) {
        if (data.deliveredAt < data.shippedAt) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Delivery date cannot be before ship date',
            path: ['deliveredAt'],
          });
        }
      }

      validateShipmentItems(data.items, ctx);
    });

// The refinement does not change the shape, so the factory's return type is
// the form's type whatever it is built with.
type FormSchema = ReturnType<typeof makeFormSchema>;

type FormData = Omit<z.infer<FormSchema>, 'shippingCost'> & {
  shippingCost: SerializableCurrency;
};
type FormOutput = z.output<FormSchema>;

const defaultCreateValues = {
  purchaseOrder: null,
  returnOrder: null,
  fromLocation: null,
  toLocation: null,
  expectedShippedOn: null,
  expectedDeliveredOn: null,
  shippedAt: null,
  deliveredAt: null,
  shipmentNote: null,
  items: [defaultShipmentFormItem],
  carrier: null,
  otherCarrier: null,
  trackingNumber: null,
};

/**
 * Data wrapper that fetches full PO and shipment data based on a PO key.
 * This component unconditionally calls hooks and should only be rendered when we have a valid key.
 */
function InboundShipmentDialogWithData(props: {
  poKey: string;
  onDataLoaded: (po: PurchaseOrder, shipments: Shipment[]) => void;
}) {
  const { poKey, onDataLoaded } = props;
  const { purchaseOrder } = usePurchaseOrder(poKey);
  const { shipments } = useShipments({
    filter: { poNumbers: [purchaseOrder.purchaseOrderNumber] },
  });

  // Notify parent when data is loaded
  useEffect(() => {
    onDataLoaded(purchaseOrder, shipments);
  }, [purchaseOrder, shipments, onDataLoaded]);

  return null; // This component just fetches data
}

/**
 * Data wrapper that fetches full RO and shipment data based on an RO key.
 * This component unconditionally calls hooks and should only be rendered when we have a valid key.
 */
function InboundShipmentDialogWithROData(props: {
  roKey: string;
  onDataLoaded: (ro: ReturnOrder, shipments: Shipment[]) => void;
}) {
  const { roKey, onDataLoaded } = props;
  const { returnOrder } = useReturnOrder(roKey);
  const { shipments } = useShipments({
    filter: { roNumbers: [returnOrder.returnOrderNumber] },
  });

  // Notify parent when data is loaded
  useEffect(() => {
    onDataLoaded(returnOrder, shipments);
  }, [returnOrder, shipments, onDataLoaded]);

  return null; // This component just fetches data
}

/**
 * Content component that handles form logic.
 * Watches the purchaseOrder/returnOrder form field and fetches data when a PO/RO is selected.
 */
function InboundShipmentDialogContent(props: Omit<Props, 'dialogTitle'>) {
  const [formErrorMessage, setFormErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();
  const defaultCurrencyCode = user.organization.defaultCurrency;
  const { showToast } = useToast();
  const { showSupportError, hideSupportError } = useSupportError();
  const isEditMode = !!props.shipment;
  const isReceived = !!props.shipment?.receivedAt;
  const isShipped = !!props.shipment?.shippedAt;
  const isDelivered = !!props.shipment?.deliveredAt;
  const isCancelled = !!props.shipment?.cancelledAt;
  const isThirdPartyBound = !!props.shipment?.thirdPartyBound;
  // The destination fixes once stock lands, by either timestamp.
  const isArrived = isDelivered || isReceived;
  // The origin fixes once stock moves, at the ship or at the receipt.
  const isOriginLocked = isShipped || isReceived;
  // An inbound shipment can be delivered without ever shipping.
  const hasShipmentLeftOpen = !!props.shipment && hasLeftOpen(props.shipment);

  // Track the last PO ID we auto-populated from (for watching PO changes)
  const [lastAutoPopulatedPoId, setLastAutoPopulatedPoId] = useState<
    string | null
  >(null);

  // Track the last RO ID we auto-populated from (for watching RO changes)
  const [lastAutoPopulatedRoId, setLastAutoPopulatedRoId] = useState<
    string | null
  >(null);

  // Store fetched PO and shipments data
  const [fullPOData, setFullPOData] = useState<{
    purchaseOrder: PurchaseOrder;
    shipments: Shipment[];
  } | null>(null);

  // Store fetched RO and shipments data
  const [fullROData, setFullROData] = useState<{
    returnOrder: ReturnOrder;
    shipments: Shipment[];
  } | null>(null);

  // Determine mode: use explicit prop, auto-detect from shipment data, or default to purchase-order
  const mode =
    props.mode ??
    (props.shipment?.returnOrder ? 'return-order' : 'purchase-order');
  const isReturnOrderMode = mode === 'return-order';

  // Track previous open state to detect open transitions
  const wasOpenRef = useRef(props.isOpen);

  const getDefaultValues = useCallback(() => {
    if (isEditMode && props.shipment) {
      // Transform purchase order dates from DateTime objects to ISO strings for schema validation
      const purchaseOrder = (
        props.shipment.purchaseOrder
          ? {
              ...props.shipment.purchaseOrder,
              promiseShipDate: props.shipment.purchaseOrder.promiseShipDate
                ? props.shipment.purchaseOrder.promiseShipDate.toISODate()
                : null,
              promiseDeliveryDate: props.shipment.purchaseOrder
                .promiseDeliveryDate
                ? props.shipment.purchaseOrder.promiseDeliveryDate.toISODate()
                : null,
            }
          : null
      ) as SparsePurchaseOrder | null;

      return {
        carrier: props.shipment.trackingNumberDetails?.carrier ?? null,
        otherCarrier: props.shipment.trackingNumberDetails?.name ?? null,
        trackingNumber:
          props.shipment.trackingNumberDetails?.trackingNumber ?? null,
        purchaseOrder: purchaseOrder,
        returnOrder: props.shipment.returnOrder ?? null,
        fromLocation: props.shipment.fromLocation,
        toLocation: props.shipment.toLocation,
        expectedShippedOn: props.shipment.expectedShippedOn,
        expectedDeliveredOn: props.shipment.expectedDeliveredOn,
        shippedAt: props.shipment.shippedAt,
        deliveredAt: props.shipment.deliveredAt,
        shipmentNote: props.shipment.shipmentNote,
        shippingCost: toCurrencyInputValue(
          props.shipment.shippingCost,
          defaultCurrencyCode
        ),
        items: mapShipmentItemsToFormItems(props.shipment.items),
      };
    }

    // In create mode, if we have a purchaseOrderKey, set up a minimal sparse PO
    // so the data fetching wrapper will load the full data
    if (props.purchaseOrderKey) {
      return {
        ...defaultCreateValues,
        shippingCost: toCurrencyInputValue(null, defaultCurrencyCode),
        purchaseOrder: {
          id: '',
          key: props.purchaseOrderKey,
          purchaseOrderNumber: '',
          externalPurchaseOrderNumber: null,
          to: null,
          from: null,
          supplier: null,
          items: [],
          promiseShipDate: null,
          promiseDeliveryDate: null,
        } as SparsePurchaseOrder,
      };
    }

    // In create mode, if we have a returnOrderKey, set up a minimal sparse RO
    // so the data fetching wrapper will load the full data
    if (props.returnOrderKey) {
      return {
        ...defaultCreateValues,
        shippingCost: toCurrencyInputValue(null, defaultCurrencyCode),
        returnOrder: {
          id: '',
          key: props.returnOrderKey,
          returnOrderNumber: '',
        },
      };
    }

    return {
      ...defaultCreateValues,
      shippingCost: toCurrencyInputValue(null, defaultCurrencyCode),
    };
  }, [
    isEditMode,
    props.shipment,
    props.purchaseOrderKey,
    props.returnOrderKey,
    defaultCurrencyCode,
  ]);

  const handleClose = () => {
    setFormErrorMessage(null);
    reset({
      ...defaultCreateValues,
      shippingCost: toCurrencyInputValue(null, defaultCurrencyCode),
    });
    setLastAutoPopulatedPoId(null);
    setLastAutoPopulatedRoId(null);
    setFullPOData(null);
    setFullROData(null);
    props.onClose();
  };

  const createShipmentMutation = useMutation({
    mutationFn: createShipment,
    onSuccess: (shipment) => {
      queryClient.invalidateQueries({ queryKey: shipmentsQueryKey });
      queryClient.invalidateQueries({ queryKey: purchaseOrdersQueryKey });
      queryClient.invalidateQueries({ queryKey: returnOrdersQueryKey });
      if (props.onSuccess) {
        props.onSuccess();
      }
      showToast({
        element: (
          <Typography>
            Shipment{' '}
            <Link
              to="/shipments/$shipmentKey"
              params={{ shipmentKey: shipment.key }}
              search={{ type: 'inbound' }}
              color="primary.main"
              sx={{ fontWeight: 'bold', textDecoration: 'underline' }}
            >
              {shipment.shipmentNumber}
            </Link>{' '}
            created
          </Typography>
        ),
        severity: 'success',
      });
      handleClose();
    },
    onError: (error) => {
      if (isPlanLimitError(error)) {
        return;
      }

      const apiError = handleAPIError(error);
      // Field-level errors: highlight the offending PO/RO field instead of a
      // generic banner so the user can correct the value in place.
      if (
        apiError.apiStatusCode ===
        ERROR_CODE_MAPPING.invalid_purchase_order_number
      ) {
        methods.setError('purchaseOrder', {
          type: 'custom',
          message: 'Purchase order not found for this organization',
        });
        return;
      }
      if (
        apiError.apiStatusCode ===
        ERROR_CODE_MAPPING.invalid_return_order_number
      ) {
        methods.setError('returnOrder', {
          type: 'custom',
          message: 'Return order not found for this organization',
        });
        return;
      }
      const inline = toDialogErrorMessage(apiError);
      if (inline !== null) {
        setFormErrorMessage(inline);
      } else {
        // 5xx or network errors - show global error snackbar, keep dialog open for retry
        if (!apiError.sentToSentry) {
          captureException(error);
        }
        showSupportError('An error occurred with the shipment.');
      }
    },
    onSettled: () => {
      props.onSubmittingChange?.(false);
    },
  });

  const updateShipmentMutation = useMutation({
    mutationFn: ({
      shipmentKey,
      data,
    }: {
      shipmentKey: string;
      data: UpdateShipmentRequest;
    }) => updateShipment(shipmentKey, data),
    onSuccess: () => {
      if (props.shipment) {
        queryClient.invalidateQueries({
          queryKey: shipmentDetailQueryKey(props.shipment.key),
        });
      }
      queryClient.invalidateQueries({ queryKey: shipmentsQueryKey });
      queryClient.invalidateQueries({ queryKey: purchaseOrdersQueryKey });
      queryClient.invalidateQueries({ queryKey: returnOrdersQueryKey });
      if (props.onSuccess) {
        props.onSuccess();
      }
      showToast({
        element: <>Shipment updated successfully</>,
        severity: 'success',
      });
      handleClose();
    },
    onError: (error) => {
      const apiError = handleAPIError(error);
      const inline = toDialogErrorMessage(apiError);
      if (inline !== null) {
        setFormErrorMessage(inline);
      } else {
        // 5xx or network errors - show global error snackbar, keep dialog open for retry
        if (!apiError.sentToSentry) {
          captureException(error);
        }
        showSupportError('An error occurred with the shipment.');
      }
    },
    onSettled: () => {
      props.onSubmittingChange?.(false);
    },
  });

  const currentOrderId =
    props.shipment?.purchaseOrder?.id ??
    props.shipment?.returnOrder?.id ??
    null;
  const formSchema = useMemo(
    () => makeFormSchema(currentOrderId),
    [currentOrderId]
  );

  const methods = useForm<FormData, unknown, FormOutput>({
    resolver: zodResolver(formSchema),
    defaultValues: getDefaultValues(),
  });

  const {
    control,
    reset,
    setValue,
    handleSubmit,
    trigger,
    formState: { dirtyFields },
  } = methods;

  // Watch the purchaseOrder field - this is the single source of truth
  const selectedPurchaseOrder = useWatch({
    control,
    name: 'purchaseOrder',
  });

  // Watch the returnOrder field - this is the single source of truth for RO mode
  const selectedReturnOrder = useWatch({
    control,
    name: 'returnOrder',
  });

  // Re-checked on selection so the 3PL refusal does not wait for a submit.
  const selectedOrderId = selectedPurchaseOrder?.id ?? selectedReturnOrder?.id;
  const selectedOrderIsThirdParty =
    selectedPurchaseOrder?.thirdParty?.isEnabled ??
    selectedReturnOrder?.thirdParty?.isEnabled;
  useEffect(() => {
    void trigger(isReturnOrderMode ? 'returnOrder' : 'purchaseOrder');
  }, [selectedOrderId, selectedOrderIsThirdParty, isReturnOrderMode, trigger]);

  // Watch fromLocation for inventory lookup (RO mode only)
  const selectedFromLocation = useWatch({
    control,
    name: 'fromLocation',
  });

  // Watch ship dates to set minimum dates on delivery date pickers
  const watchedExpectedShippedOn = useWatch({
    control,
    name: 'expectedShippedOn',
  });

  const watchedShippedAt = useWatch({
    control,
    name: 'shippedAt',
  });

  const watchedDeliveredAt = useWatch({
    control,
    name: 'deliveredAt',
  });

  const watchedCarrier = useWatch({
    control,
    name: 'carrier',
  });

  // Fetch inventory at fromLocation for RO mode
  const {
    inventoryMap,
    isLoading: isLoadingInventory,
    isFetching: isFetchingInventory,
  } = useReturnOrderInventory({
    location: isReturnOrderMode ? selectedFromLocation : undefined,
  });

  // Reset form when dialog opens (transitions from closed to open) or when shipment changes
  useEffect(() => {
    const isOpening = props.isOpen && !wasOpenRef.current;
    wasOpenRef.current = props.isOpen;

    if (isOpening) {
      reset(getDefaultValues());
      // Reset auto-populate tracking so data will be re-populated
      // Don't clear fullPOData/fullROData - the child fetcher will update them
      // and clearing here causes a race condition where child's effect runs first
      setLastAutoPopulatedPoId(null);
      setLastAutoPopulatedRoId(null);
    }
  }, [props.isOpen, props.shipment, isEditMode, reset, getDefaultValues]);

  // Get the PO key from the selected PO in the form field
  const selectedPOKey = selectedPurchaseOrder?.key;

  // Clear cached data when PO selection is cleared (so re-selecting will re-populate)
  useEffect(() => {
    if (!selectedPOKey) {
      setFullPOData(null);
      setLastAutoPopulatedPoId(null);
    }
  }, [selectedPOKey]);

  // Stable callback for data fetching wrapper
  const handlePODataLoaded = useCallback(
    (po: PurchaseOrder, shipments: Shipment[]) => {
      setFullPOData({ purchaseOrder: po, shipments });
    },
    []
  );

  // Auto-populate form when full PO data is loaded
  useEffect(() => {
    if (isEditMode || !fullPOData) return;

    // Skip if we already populated from this PO
    if (fullPOData.purchaseOrder.id === lastAutoPopulatedPoId) return;

    // Set locations from PO
    if (fullPOData.purchaseOrder.fromLocation) {
      setValue('fromLocation', fullPOData.purchaseOrder.fromLocation);
    }
    setValue('toLocation', fullPOData.purchaseOrder.toLocation);

    // Convert PO items to form items with "need" quantity pre-filled
    const formItems = fullPOData.purchaseOrder.items.map((lineItem) => {
      const quantities = calculateShipmentQuantities(
        fullPOData.shipments,
        lineItem.item.id
      );
      const fulfilled =
        quantities.pending +
        quantities.shipped +
        quantities.delivered +
        quantities.received;
      const need = Math.max(0, lineItem.requestedQuantity - fulfilled);

      return { item: extractSparseItem(lineItem), quantity: need };
    });

    setValue('items', formItems);

    // Mark this PO as auto-populated
    setLastAutoPopulatedPoId(fullPOData.purchaseOrder.id);
  }, [fullPOData, isEditMode, setValue, lastAutoPopulatedPoId]);

  // Get the RO key from the selected RO in the form field
  const selectedROKey = selectedReturnOrder?.key;

  // Clear cached data when RO selection is cleared (so re-selecting will re-populate)
  useEffect(() => {
    if (!selectedROKey) {
      setFullROData(null);
      setLastAutoPopulatedRoId(null);
    }
  }, [selectedROKey]);

  // Stable callback for RO data fetching wrapper
  const handleRODataLoaded = useCallback(
    (ro: ReturnOrder, shipments: Shipment[]) => {
      setFullROData({ returnOrder: ro, shipments });
      // Also set the form value if not already set (resolves the RO for the search field)
      setValue('returnOrder', ro);
    },
    [setValue]
  );

  // Auto-populate form when full RO data is loaded
  useEffect(() => {
    if (isEditMode || !fullROData) return;

    // Skip if we already populated from this RO
    if (fullROData.returnOrder.id === lastAutoPopulatedRoId) return;

    // Set locations from RO (from = customer location, to = warehouse)
    if (fullROData.returnOrder.fromLocation) {
      setValue('fromLocation', fullROData.returnOrder.fromLocation);
    }
    setValue('toLocation', fullROData.returnOrder.toLocation);

    // Convert RO items to form items with "need" quantity pre-filled
    const formItems = fullROData.returnOrder.items.map((lineItem) => {
      const quantities = calculateShipmentQuantities(
        fullROData.shipments,
        lineItem.item.id
      );
      const fulfilled =
        quantities.pending +
        quantities.shipped +
        quantities.delivered +
        quantities.received;
      const requestedQuantity = lineItem.requestedQuantity ?? 0;
      const need = Math.max(0, requestedQuantity - fulfilled);

      return { item: extractSparseItem(lineItem), quantity: need };
    });

    setValue('items', formItems);

    // Mark this RO as auto-populated
    setLastAutoPopulatedRoId(fullROData.returnOrder.id);
  }, [fullROData, isEditMode, setValue, lastAutoPopulatedRoId]);

  const onSubmit = async (data: FormOutput) => {
    setFormErrorMessage(null);
    hideSupportError();
    props.onSubmittingChange?.(true);

    const items = data.items
      .filter((item) => item.item)
      .map((item) => ({
        itemId: item.item?.id || '',
        quantity: item.quantity,
      }));

    const expectedShippedOn = data.expectedShippedOn;
    const expectedDeliveredOn = data.expectedDeliveredOn;
    const shippedAt = data.shippedAt ? data.shippedAt.toISO() : null;
    const shippedAtTimezone = data.shippedAt ? data.shippedAt.zoneName : null;
    const deliveredAt = data.deliveredAt ? data.deliveredAt.toISO() : null;
    const deliveredAtTimezone = data.deliveredAt
      ? data.deliveredAt.zoneName
      : null;

    if (isEditMode && props.shipment) {
      const updateData: UpdateShipmentRequest = {
        expectedShippedOn: expectedShippedOn,
        expectedDeliveredOn: expectedDeliveredOn,
        shippedAt: shippedAt,
        shippedAtTimezone: shippedAtTimezone,
        deliveredAt: deliveredAt,
        deliveredAtTimezone: deliveredAtTimezone,
        shipmentNote: data.shipmentNote,
      };

      // Sending a manifest the form never showed claims a change the user did not make.
      if (canEditManifestInForm(props.shipment)) {
        updateData.items = items;
      }

      if (dirtyFields.shippingCost) {
        updateData.shippingCost = data.shippingCost ?? null;
      }

      // Only include purchaseOrder/returnOrder while the shipment is still open
      if (!hasShipmentLeftOpen) {
        updateData.purchaseOrderNumber =
          data.purchaseOrder?.purchaseOrderNumber ?? null;
        updateData.returnOrderNumber =
          data.returnOrder?.returnOrderNumber ?? null;
      }

      // Only include fromLocationId if no stock has moved
      if (!isOriginLocked) {
        updateData.fromLocationId = data.fromLocation?.id ?? null;
      }

      // Only include toLocationId if the shipment hasn't arrived
      if (!isArrived) {
        updateData.toLocationId = data.toLocation?.id ?? null;
      }

      // Only include the carrier if not delivered
      if (!isDelivered) {
        updateData.trackingNumberDetails = toTrackingNumberDetailsRequest(
          data.carrier,
          data.otherCarrier,
          data.trackingNumber
        );
      }

      return updateShipmentMutation.mutate({
        shipmentKey: props.shipment.key,
        data: updateData,
      });
    }
    const createData = {
      shipmentType: 'inbound' as const,
      items: items,
      toLocationId: data.toLocation?.id || '',
      purchaseOrderNumber: data.purchaseOrder?.purchaseOrderNumber ?? null,
      returnOrderNumber: data.returnOrder?.returnOrderNumber ?? null,
      trackingNumberDetails: toTrackingNumberDetailsRequest(
        data.carrier,
        data.otherCarrier,
        data.trackingNumber
      ),
      fromLocationId: data.fromLocation?.id,
      expectedShippedOn: expectedShippedOn,
      expectedDeliveredOn: expectedDeliveredOn,
      shippedAt: shippedAt,
      shippedAtTimezone: shippedAtTimezone,
      deliveredAt: deliveredAt,
      deliveredAtTimezone: deliveredAtTimezone,
      shipmentNote: data.shipmentNote,
      shippingCost: data.shippingCost ?? null,
    };
    return createShipmentMutation.mutate(createData);
  };

  const isOtherCarrier = watchedCarrier === ('other' as ShippingCarrier);

  const renderSectionHeader = (label: string, marginTop = 0) => (
    <Grid item xs={12}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mt: marginTop,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontWeight: 700,
            letterSpacing: 0,
            lineHeight: 1,
            textTransform: 'uppercase',
          }}
        >
          {label}
        </Typography>
        <Divider sx={{ flex: 1 }} />
      </Box>
    </Grid>
  );

  return (
    <FormProvider {...methods}>
      {/* Data fetching wrapper - only rendered when we have a PO key (PO mode) */}
      {!isReturnOrderMode && selectedPOKey && (
        <Suspense fallback={null}>
          <InboundShipmentDialogWithData
            poKey={selectedPOKey}
            onDataLoaded={handlePODataLoaded}
          />
        </Suspense>
      )}

      {/* Data fetching wrapper - rendered when we have an RO key from form or props (RO mode) */}
      {/* SelectedROKey takes priority - it reflects user's current selection in the dropdown */}
      {isReturnOrderMode && (selectedROKey || props.returnOrderKey) && (
        <Suspense fallback={null}>
          <InboundShipmentDialogWithROData
            roKey={(selectedROKey || props.returnOrderKey)!}
            onDataLoaded={handleRODataLoaded}
          />
        </Suspense>
      )}

      <form id="add-inbound-shipment-form" onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          {renderSectionHeader('Source')}

          <Grid item xs={12} md={6}>
            <ConditionalTooltip
              arrow
              tooltipText={
                isCancelled
                  ? CANCELLED_LOCK_REASON
                  : isThirdPartyBound
                    ? MANAGED_ORDER_LOCK_REASON
                    : hasShipmentLeftOpen
                      ? isReturnOrderMode
                        ? 'Return order cannot be modified once the shipment is no longer open'
                        : 'Purchase order cannot be modified once the shipment is no longer open'
                      : undefined
              }
              sx={{ width: '100%' }}
            >
              <Box sx={{ width: '100%' }}>
                {isReturnOrderMode ? (
                  <FormReturnOrderSearch
                    name="returnOrder"
                    label="Return order"
                    control={control}
                    disabled={
                      hasShipmentLeftOpen || isCancelled || isThirdPartyBound
                    }
                    keyToResolve={props.returnOrderKey ?? undefined}
                  />
                ) : (
                  <FormPurchaseOrderSearch
                    name="purchaseOrder"
                    label="Purchase order"
                    control={control}
                    disabled={
                      hasShipmentLeftOpen || isCancelled || isThirdPartyBound
                    }
                  />
                )}
              </Box>
            </ConditionalTooltip>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormCurrencyTextField
              control={control}
              fullWidth
              label="Shipping cost"
              name="shippingCost"
              placeholder="0.00"
            />
          </Grid>

          {renderSectionHeader('Carrier', 1)}

          <Grid item xs={12} md={6}>
            <ConditionalTooltip
              arrow
              tooltipText={
                isCancelled
                  ? CANCELLED_LOCK_REASON
                  : isDelivered
                    ? 'Carrier cannot be modified after shipment has been delivered'
                    : undefined
              }
              sx={{ width: '100%' }}
            >
              <Box sx={{ width: '100%' }}>
                <FormShippingCarrierSearch
                  name="carrier"
                  label="Carrier"
                  control={control}
                  disabled={isDelivered || isCancelled}
                />
              </Box>
            </ConditionalTooltip>
            <Collapse
              in={!isOtherCarrier}
              sx={{
                marginLeft: 'auto',
                marginRight: 2,
                paddingTop: 0.5,
              }}
            >
              <Controller
                name={`carrier`}
                control={control}
                render={({ field }) => {
                  const handleClick = () => {
                    field.onChange('other');
                  };
                  return (
                    <Typography
                      role="button"
                      variant="body2"
                      color="primary.main"
                      onClick={handleClick}
                      sx={{ cursor: 'pointer', textAlign: 'right' }}
                    >
                      Enter manually
                    </Typography>
                  );
                }}
              />
            </Collapse>
            <Collapse
              in={isOtherCarrier}
              sx={{
                marginLeft: 'auto',
                paddingTop: 0.5,
              }}
            >
              <FormTextField
                fullWidth
                name={'otherCarrier'}
                label="Name"
                control={control}
                disabled={isCancelled}
              />
            </Collapse>
          </Grid>
          <Grid item xs={12} md={6}>
            <ConditionalTooltip
              arrow
              tooltipText={
                isCancelled
                  ? CANCELLED_LOCK_REASON
                  : isDelivered
                    ? 'Tracking number cannot be modified after shipment has been delivered'
                    : undefined
              }
              sx={{ width: '100%' }}
            >
              <Box sx={{ width: '100%' }}>
                <FormTextField
                  fullWidth
                  name="trackingNumber"
                  label="Tracking number"
                  control={control}
                  disabled={isDelivered || isCancelled}
                />
              </Box>
            </ConditionalTooltip>
          </Grid>

          {renderSectionHeader('Route', 1)}

          <Grid item xs={12} md={6}>
            <ConditionalTooltip
              arrow
              tooltipText={
                isCancelled
                  ? CANCELLED_LOCK_REASON
                  : isOriginLocked
                    ? 'From location cannot be modified once stock has moved'
                    : undefined
              }
              sx={{ width: '100%' }}
            >
              <Box sx={{ width: '100%' }}>
                <FormLocationSearch
                  name={'fromLocation'}
                  control={control}
                  label="From location"
                  grouping={['non-inventory']}
                  disabled={isOriginLocked || isCancelled}
                  onCreate={(name: string) => {
                    const url = getNewLocationURL(name);
                    window.open(url, '_blank');
                  }}
                />
              </Box>
            </ConditionalTooltip>
          </Grid>

          <Grid item xs={12} md={6}>
            <ConditionalTooltip
              arrow
              tooltipText={
                isCancelled
                  ? CANCELLED_LOCK_REASON
                  : isArrived
                    ? 'To location cannot be modified after shipment has been delivered'
                    : undefined
              }
              sx={{ width: '100%' }}
            >
              <Box sx={{ width: '100%' }}>
                <FormLocationSearch
                  name={'toLocation'}
                  control={control}
                  label="To location"
                  grouping={['inventory']}
                  disabled={isArrived || isCancelled}
                  onCreate={(name: string) => {
                    const url = getNewLocationURL(name);
                    window.open(url, '_blank');
                  }}
                />
              </Box>
            </ConditionalTooltip>
          </Grid>

          {renderSectionHeader('Timeline', 1)}

          {/* Expected ship date */}
          <Grid item xs={12} md={6}>
            <FormDatePicker
              name="expectedShippedOn"
              label="Expected ship date"
              control={control}
              monthsToShow={2}
              disabled={isCancelled}
            />
            {fullPOData?.purchaseOrder?.promiseShipDate && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textAlign: 'right', display: 'block', mt: 0.5, pr: 1 }}
              >
                Promise date:{' '}
                {fullPOData.purchaseOrder.promiseShipDate.toLocaleString({
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </Typography>
            )}
            {fullROData?.returnOrder?.promiseShipDate && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textAlign: 'right', display: 'block', mt: 0.5, pr: 1 }}
              >
                Promise date:{' '}
                {fullROData.returnOrder.promiseShipDate.toLocaleString({
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </Typography>
            )}
          </Grid>

          {/* Expected delivery date */}
          <Grid item xs={12} md={6}>
            <FormDatePicker
              name="expectedDeliveredOn"
              label="Expected delivery date"
              control={control}
              disabled={isCancelled}
              minimumDate={watchedExpectedShippedOn ?? undefined}
              monthsToShow={2}
            />
            {fullPOData?.purchaseOrder?.promiseDeliveryDate && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textAlign: 'right', display: 'block', mt: 0.5, pr: 1 }}
              >
                Promise date:{' '}
                {fullPOData.purchaseOrder.promiseDeliveryDate.toLocaleString({
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </Typography>
            )}
            {fullROData?.returnOrder?.promiseDeliveryDate && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textAlign: 'right', display: 'block', mt: 0.5, pr: 1 }}
              >
                Promise date:{' '}
                {fullROData.returnOrder.promiseDeliveryDate.toLocaleString({
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </Typography>
            )}
          </Grid>

          {/* Shipped At */}
          <Grid item xs={12} md={6}>
            <ConditionalTooltip
              arrow
              tooltipText={
                isCancelled
                  ? CANCELLED_LOCK_REASON
                  : isDelivered
                    ? 'Shipped at cannot be modified after shipment has been delivered'
                    : undefined
              }
              sx={{ width: '100%' }}
            >
              <Box sx={{ width: '100%' }}>
                <FormDateTimePicker
                  disableFuture
                  name="shippedAt"
                  label="Shipped at"
                  control={control}
                  isDisabled={isDelivered || isCancelled}
                  isClearable={!props.shipment?.shippedAt}
                  maximumDate={
                    watchedDeliveredAt ??
                    props.shipment?.receivedAt ??
                    undefined
                  }
                />
              </Box>
            </ConditionalTooltip>
          </Grid>

          {/* Delivered At */}
          <Grid item xs={12} md={6}>
            <ConditionalTooltip
              arrow
              tooltipText={
                isCancelled
                  ? CANCELLED_LOCK_REASON
                  : isEditMode && !isArrived
                    ? 'Mark the shipment delivered to record its delivery'
                    : undefined
              }
              sx={{ width: '100%' }}
            >
              <Box sx={{ width: '100%' }}>
                <FormDateTimePicker
                  disableFuture
                  name="deliveredAt"
                  label="Delivered at"
                  control={control}
                  isDisabled={isCancelled || (isEditMode && !isArrived)}
                  isClearable={!props.shipment?.deliveredAt}
                  minimumDate={watchedShippedAt ?? undefined}
                  maximumDate={props.shipment?.receivedAt ?? undefined}
                />
              </Box>
            </ConditionalTooltip>
          </Grid>

          {renderSectionHeader('Notes', 1)}

          <Grid item xs={12}>
            <FormTextField
              fullWidth
              isMultiline
              minimumRows={3}
              name="shipmentNote"
              label="Shipment notes"
              placeholder={'Optional notes about this shipment'}
              control={control}
            />
          </Grid>

          {/* Shipment items - only while the manifest is still editable */}
          {(!props.shipment || canEditManifestInForm(props.shipment)) && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <InboundShipmentItemsTable
                name="items"
                isShipped={isShipped}
                isReceived={isReceived}
                purchaseOrder={fullPOData?.purchaseOrder || null}
                returnOrder={fullROData?.returnOrder || null}
                shipments={fullPOData?.shipments || fullROData?.shipments || []}
                fromLocationId={
                  isReturnOrderMode ? selectedFromLocation?.id : undefined
                }
                inventoryMap={isReturnOrderMode ? inventoryMap : undefined}
                isLoadingInventory={isLoadingInventory}
                isFetchingInventory={isFetchingInventory}
              />
            </Grid>
          )}
        </Grid>
      </form>
      {formErrorMessage && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: 'error.light',
            borderRadius: 1,
            color: 'error.contrastText',
          }}
        >
          <Typography variant="body2">{formErrorMessage}</Typography>
        </Box>
      )}
    </FormProvider>
  );
}

/**
 * Dialog for adding a new inbound shipment or editing an existing one.
 * Fetches all necessary data internally based on the purchaseOrderKey prop.
 */
export function InboundShipmentDialog(props: Props) {
  const isEditMode = !!props.shipment;

  // Track whether a mutation is in-flight so the submit button can show a loading state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Compute whether editing will cascade to asset event groups
  const willCascade =
    isEditMode && props.shipment
      ? hasAssetEventGroupCascade(props.shipment, 'inbound')
      : false;

  // Calculate serialized asset count and estimated cascade time
  const serializedAssetCount =
    isEditMode && props.shipment
      ? getTotalSerializedAssetCount(props.shipment.items)
      : 0;
  const estimatedCascadeSeconds =
    calculateCascadeTimeSeconds(serializedAssetCount);

  return (
    <Dialog
      key={props.shipment?.key} // Force remount when shipment changes
      fullWidth
      maxWidth="lg"
      open={props.isOpen}
      onClose={props.onClose}
      scroll="paper"
    >
      <DialogTitle>
        {props.dialogTitle ??
          (isEditMode ? 'Edit inbound shipment' : 'Add inbound shipment')}
      </DialogTitle>
      <DialogContent dividers>
        <Suspense
          fallback={
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={400}
            >
              <CircularProgress />
            </Box>
          }
        >
          <InboundShipmentDialogContent
            {...props}
            onSubmittingChange={setIsSubmitting}
          />
        </Suspense>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {/* Cascade warning - shown when editing will cascade to AEGs */}
        <Box display="flex" alignItems="center">
          {willCascade && serializedAssetCount > 0 && (
            <Alert
              icon={<InfoOutlinedIcon fontSize="small" />}
              severity="info"
              sx={{
                py: 0.5,
                px: 1.5,
                alignItems: 'center',
                '& .MuiAlert-message': { py: 0 },
              }}
            >
              <Typography variant="body2" sx={{ lineHeight: 1 }}>
                Editing this shipment will update{' '}
                {pluralize(serializedAssetCount, { singular: 'asset' })}
              </Typography>
            </Alert>
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          {/* Estimated time - shown immediately left of Cancel */}
          {willCascade && estimatedCascadeSeconds > 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.5 }}
            >
              Estimated time: {formatDuration(estimatedCascadeSeconds)}
            </Typography>
          )}
          <Button onClick={props.onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="add-inbound-shipment-form"
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            startIcon={
              isSubmitting ? <CircularProgress size={16} /> : undefined
            }
          >
            {isSubmitting
              ? 'Updating...'
              : isEditMode
                ? 'Edit shipment'
                : 'Create shipment'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
